'use client'

import { useCallback, useState } from 'react'

import { SEARCH_PARAM_KEYS } from '@/constants/auth'
import useCheckLoginStatus from '@/hooks/auth/useCheckLoginStatus'
import { useHandleGeneralProfile } from '@/hooks/auth/useHandleGeneralProfile'
import useHandleProfileResult from '@/hooks/auth/useHandleProfileResult'
import useIsSignedIn from '@/hooks/auth/useIsSignedIn'
import { createCredentials, removeKeysFromSearchParams } from '@/lib/auth'
import { credentialsSchema } from '@/schemas/auth'

const useCheckAuth = (deps?: { onLogout?: () => void }) => {
  // 🥷🏼 currently use this way to notify profile page as workaround
  // 🥷🏼 when preparing auth check
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [isSignedIn, setIsSignedIn] = useIsSignedIn()

  const checkLoginStatus = useCheckLoginStatus(deps?.onLogout)
  const handleProfileResult = useHandleProfileResult(setIsSignedIn)
  const handleGeneralProfile = useHandleGeneralProfile(setIsSignedIn)

  const checkAuth = useCallback(async () => {
    const qs = new URLSearchParams(location.search)
    const qsHtcToken = qs.get(SEARCH_PARAM_KEYS.HTC_TOKEN)

    try {
      await checkLoginStatus(qs)
      const credentials = createCredentials(qs)

      if (credentialsSchema.safeParse(credentials).success) {
        await handleProfileResult(credentials, qsHtcToken)
      } else {
        if (process.env.NODE_ENV !== 'development') {
          await handleGeneralProfile()
        }
      }
    } catch (error) {
      setIsSignedIn(false)
      console.error('Error while checking auth: ', error)
      // TODO: handle error
      // goErrorPage()
    } finally {
      const searchParamsToRemove = Object.values(SEARCH_PARAM_KEYS).filter(
        // 🥷🏼 filter create to let it be used in CreateWorld.tsx
        (key) =>
          key !== 'create' &&
          key !== 'template' &&
          key !== 'preview' &&
          key !== 'app_state' &&
          key !== 'state',
      )
      removeKeysFromSearchParams(searchParamsToRemove)
      setIsCheckingAuth(false)
    }
  }, [checkLoginStatus, handleGeneralProfile, handleProfileResult, setIsSignedIn])

  return {
    isSignedIn,
    isCheckingAuth,
    checkAuth,
    setIsSignedIn,
  }
}

export default useCheckAuth
